import React, { useState, useEffect } from 'react';
import {
  ShieldCheck, Calendar, FileText, CheckCircle2,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface PrivacyConsentCardProps {
  client: {
    privacy_consent_given?: boolean;
    privacy_consent_date?: string;
    privacy_consent_form_id?: string;
    privacy_consent_text?: string;
  };
}

function PropertyRow({ icon: Icon, label, value }: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 py-1.5 px-2 min-h-[28px]">
      <Icon className="h-3.5 w-3.5 text-[#787774] dark:text-gray-500 flex-shrink-0" />
      <span className="text-xs text-[#787774] dark:text-gray-400 w-[100px] flex-shrink-0">{label}</span>
      <span className="text-sm text-[#37352F] dark:text-white truncate">{value || '-'}</span>
    </div>
  );
}

export function PrivacyConsentCard({ client }: PrivacyConsentCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [formName, setFormName] = useState<string | null>(null);

  if (!client.privacy_consent_given) return null;

  useEffect(() => {
    if (client.privacy_consent_form_id) {
      supabase
        .from('lead_forms')
        .select('name')
        .eq('id', client.privacy_consent_form_id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) setFormName(data.name);
        });
    }
  }, [client.privacy_consent_form_id]);

  // Riga di proprietà (redesign pannello 13/08): label "Privacy" + badge verde
  // come valore, dettagli (data, modulo, testo) nell'espansione al click.
  return (
    <div className="space-y-1">
      <div
        className="group flex min-h-[30px] cursor-pointer items-center rounded px-1.5 py-0.5 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06]"
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); } }}
      >
        <div className="flex w-32 shrink-0 items-center gap-1.5 text-xs font-medium text-[#787774] dark:text-gray-400">
          <ShieldCheck className="h-3 w-3" strokeWidth={1.5} />
          <span>Privacy</span>
        </div>
        <div className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px] text-[#37352F] dark:text-white">
          <span className="rounded bg-green-100 px-1.5 py-0.5 text-[11px] text-green-700 dark:bg-green-900/30 dark:text-green-400">
            Consenso dato
          </span>
          {client.privacy_consent_date && (
            <span className="truncate text-[12px] text-[rgba(55,53,47,0.5)] dark:text-gray-400">
              · {new Date(client.privacy_consent_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          )}
          {expanded ? <ChevronDown className="h-3 w-3 flex-shrink-0 text-[#787774]" /> : <ChevronRight className="h-3 w-3 flex-shrink-0 text-[#787774]" />}
        </div>
      </div>

      {expanded && (
        <div className="space-y-0.5 ml-1">
          {/* Consent status */}
          <PropertyRow
            icon={CheckCircle2}
            label="Consenso"
            value={
              <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
                Dato
              </span>
            }
          />

          {/* Consent date */}
          {client.privacy_consent_date && (
            <PropertyRow
              icon={Calendar}
              label="Data"
              value={new Date(client.privacy_consent_date).toLocaleDateString('it-IT', {
                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            />
          )}

          {/* Form origin */}
          {(formName || client.privacy_consent_form_id) && (
            <PropertyRow
              icon={FileText}
              label="Modulo"
              value={formName || client.privacy_consent_form_id}
            />
          )}

          {/* Consent text */}
          {client.privacy_consent_text && (
            <div className="px-2 py-1.5">
              <div className="text-xs text-[#787774] dark:text-gray-400 mb-1">Testo accettato</div>
              <div
                className="text-xs text-[#37352F]/70 dark:text-gray-300/70 bg-[rgba(55,53,47,0.04)] dark:bg-white/[0.03] rounded p-2 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: client.privacy_consent_text }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
